import * as React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { before, capitalize, memoize } from 'lodash';
import Button from '@atlaskit/button/standard-button';
import AddIcon from '@atlaskit/icon/glyph/add';
import InfoIcon from '@atlaskit/icon/glyph/info';
import SectionMessage from '@atlaskit/section-message';
import Tooltip from '@atlaskit/tooltip';
import { N500A } from '@atlaskit/theme/colors';
import {
  DynamicResultTable,
  NoDataContentInTable,
  OpenSidebarButton,
  getCustomPromotionReason,
  getPromotionDuration,
  getPromotionTitle,
  renderPromotionValue,
  useFeatureToggle,
} from '@css-xp/base';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { Maybe, Promotion } from '@css-xp/graphtypes';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { NO_DETAILS_TO_SHOW, PARTNER_DISCOUNT, isDeletedEntitlement } from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_SANDBOX_ENTITLEMENT } from '@atlassian/cssxp-utils-generic';
import { isBilledElsewhereProduct } from '@atlassian/cssxp-utils-product';
import { SIDEBAR } from '../../../../entitlement-page';
import { PREDICATES } from '../../../constants';
import { getPredicates } from '../../../utils';
import './applied-promotions-table.scss';

export interface AppliedPromotions extends Promotion {
  billingCycle?: string;
}

export const emptyStateProps = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
};
interface Props {
  promotions: Maybe<AppliedPromotions>[];
  entitlement?: EntitlementDetails;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}

const getHead = memoize(() => {
  const columns = [
    {
      content: 'Discount',
      width: 35,
    },
    {
      content: 'Details',
      width: 30,
    },
    {
      content: 'Billing cycle',
      width: 10,
    },
    {
      content: 'Duration',
      width: 10,
    },
    {
      content: '',
      width: 10,
    },
  ];

  return {
    cells: columns,
  };
});

// Currently pulls every billing admin, will want to move

const AppliedPromotionsTable = ({ entitlement, promotions, refetch }: Props) => {
  const transactionAccountContext = React.useContext(TransactionAccountContext);

  const sidebarOptions = {
    sidebarWidth: '600px',
    isOverlay: true,
  };
  const payload = {
    entitlement,
    transactionAccountContext,
    refetch,
  };
  const [isEnabled] = useFeatureToggle();

  const isOnUpgradeTrial = !!entitlement?.upgradeTrial;

  const isSandbox = isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && entitlement?.isSandbox;

  const renderPromotionTitle = (promotion?: Promotion | null) => {
    const promotionTitle = getPromotionTitle(promotion, { handleEmptyTitle: true });
    if (promotion?.type === PARTNER_DISCOUNT) {
      return (
        <>
          <span className="promotion-title">{promotionTitle}</span>
          {promotion?.applicationReason?.readableName && (
            <Tooltip content={getCustomPromotionReason(promotion)} tag={'span'}>
              <InfoIcon size="small" testId="info-icon" label="Info" primaryColor={N500A} />
            </Tooltip>
          )}
        </>
      );
    }
    return promotionTitle;
  };

  const renderRemoveButton = (promotion?: Promotion | null) => {
    const isDisabled = isOnUpgradeTrial || promotion?.type === PARTNER_DISCOUNT;
    if (getPredicates(entitlement, PREDICATES.REMOVE_PROMOTION) && !isSandbox && !isDeletedEntitlement(entitlement)) {
      return (
        <OpenSidebarButton
          sidebar={SIDEBAR.REMOVE_PROMOTION}
          sidebarOptions={sidebarOptions}
          payload={payload}
          appearance="link"
          testId="remove-promotion"
          isDisabled={isDisabled}
          label="Remove"
        >
          <div className={`${isDisabled ? 'disabled' : 'remove-promotion-button'}`}>
            <Button appearance="link">Remove</Button>
          </div>
        </OpenSidebarButton>
      );
    }
    return null;
  };

  const getRows = () => {
    return promotions.map((promotion, index) => {
      return {
        key: `applied-promotion-${index}`,
        testId: `applied-promotion-details`,
        cells: [
          {
            key: 'type' + index,
            content: renderPromotionTitle(promotion),
          },
          {
            key: 'details' + index,
            content: renderPromotionValue(promotion, { showDuration: false, showReason: true }),
          },
          {
            key: 'billing cycle' + index,
            content: promotion?.billingCycle ? capitalize(promotion?.billingCycle) : <NoDataContentInTable />,
          },
          {
            key: 'duration' + index,
            content: promotion?.benefits?.[0] ? getPromotionDuration(promotion.benefits[0]) : <NoDataContentInTable />,
          },
          {
            key: 'remove' + index,
            content: renderRemoveButton(promotion),
          },
        ],
      };
    });
  };

  const beforeTable = () => {
    if (isOnUpgradeTrial) {
      return (
        <SectionMessage appearance="warning">
          <p>No discounts can be added to or removed from the existing plan during an ongoing trial</p>
        </SectionMessage>
      );
    }

    return null;
  };

  const addButton =
    getPredicates(entitlement, PREDICATES.ADD_PROMOTIONS) &&
    !isSandbox &&
    !isDeletedEntitlement(entitlement) &&
    !isBilledElsewhereProduct(entitlement?.productId) ? (
      <OpenSidebarButton
        sidebar={SIDEBAR.APPLY_PROMOTION}
        sidebarOptions={sidebarOptions}
        payload={payload}
        appearance="link"
        iconBefore={<AddIcon label="Add icon" size="small" />}
        testId="add-promotion"
        isDisabled={isOnUpgradeTrial}
        label="Add promotions"
      >
        <span aria-label="Add promotions">Add</span>
      </OpenSidebarButton>
    ) : null;

  return (
    <div className="applied-promotion-details-table">
      <DynamicResultTable
        testId="applied-promotion-details"
        head={getHead()}
        rows={getRows()}
        beforeTable={beforeTable()}
        header={
          <div className="flex-container">
            <h4>Active discounts</h4>
            <Tooltip position="bottom" content="Includes discounts that will be applied to the next bill">
              <InfoIcon label="info" size="small" />
            </Tooltip>
          </div>
        }
        emptyStateProps={emptyStateProps}
        loadingSpinnerSize="large"
        isLoading={false}
        footer={addButton}
      />
    </div>
  );
};

export default AppliedPromotionsTable;
